import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Search, Clock, BookOpen, MessageCircle, ChevronRight,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCatalog } from '../context/CatalogContext';
import { getCategoryContent } from '../data/categoryLandingContent';

/**
 * Category → Individual Treatment → Request Appointment.
 * No clinic counts, no directory links — treatments are the product.
 *
 * Data source: API-first via `useCatalog()`; transparently falls back to the
 * bundled static catalog when the API is unreachable or empty.
 */
const TreatmentsPage = () => {
  const [query, setQuery] = useState('');
  const { categories: CATEGORIES, treatmentsDisplay: TREATMENTS, getCategoryImage } = useCatalog();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return TREATMENTS.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.shortDesc || '').toLowerCase().includes(q) ||
        (t.category || '').toLowerCase().includes(q)
    );
  }, [query, TREATMENTS]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Treatments in Vietnam — Dental, Hair, Cosmetic, IVF & More | VietClinics</title>
        <meta name="description" content="Explore 46+ premium treatments across 7 medical categories in Vietnam. Concierge-matched specialists, verified providers, no clinic browsing required." />
        <meta property="og:title" content="Explore Treatments in Vietnam — VietClinics" />
        <meta property="og:description" content="Premium care across Dental, Hair Restoration, Cosmetic Surgery, Aesthetic & Skin, Fertility, Eye Care, and Preventive Care. Request a personalised consultation." />
        <link rel="canonical" href="https://vietclinics.com/treatments" />
      </Helmet>
      <Header />

      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0f2b55 0%, #1E5BA8 55%, #1FA7A1 100%)' }}
      >
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 0%, transparent 50%), radial-gradient(circle at 10% 80%, white 0%, transparent 45%)' }}
        />
        <div className="container mx-auto px-4 max-w-5xl py-16 lg:py-24 text-center relative z-10">
          <p className="text-[11px] font-bold text-white/70 uppercase tracking-widest mb-4">
            Treatments First · Concierge Care
          </p>
          <h1
            className="font-extrabold tracking-tight leading-[1.05] text-white"
            style={{ fontSize: 'clamp(2.4rem, 4.8vw, 3.8rem)' }}
            data-testid="treatments-hero-title"
          >
            Explore Treatments in Vietnam
          </h1>
          <p className="text-white/85 text-lg mt-5 leading-relaxed max-w-2xl mx-auto">
            Premium care across 7 medical categories — every treatment matched to a verified specialist by the VietClinics concierge team.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search treatments — e.g. Implants, Rhinoplasty, IVF..."
                className="w-full h-12 pl-11 pr-4 rounded-full bg-white/95 backdrop-blur text-slate-800 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-white/40"
                data-testid="treatments-search"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <a
              href="/#request-appointment"
              className="bg-white text-[#1FA7A1] font-bold rounded-full px-7 py-3 inline-flex items-center gap-2 hover:-translate-y-0.5 transition-all"
              style={{ boxShadow: '0 8px 20px rgba(0,0,0,0.10)' }}
              data-testid="treatments-hero-request"
            >
              Request Consultation <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to="/find-best-treatment"
              className="text-white/95 font-semibold rounded-full px-7 py-3 inline-flex items-center gap-2 border border-white/30 backdrop-blur-sm hover:bg-white/15 transition-all"
              data-testid="treatments-hero-ai"
            >
              Find the right treatment for me
            </Link>
          </div>
        </div>
      </section>

      {/* ── CATEGORY CHIPS NAV ─────────────────────────────────────────── */}
      <section className="bg-white border-b border-slate-100 sticky top-[64px] z-30">
        <div className="container mx-auto px-4 max-w-6xl py-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`#cat-${c.slug}`}
                className="flex-shrink-0 text-xs sm:text-sm font-semibold text-slate-600 hover:text-[#1FA7A1] px-4 py-2 rounded-full border border-slate-200 hover:border-[#1FA7A1]/40 hover:bg-[#1FA7A1]/5 transition-all"
                data-testid={`category-chip-${c.slug}`}
              >
                {c.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEARCH RESULTS ──────────────────────────────────────────────── */}
      {filtered && (
        <section className="py-12 bg-white" data-testid="search-results-section">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-6">
              {filtered.length === 0
                ? `No treatments match "${query}"`
                : `${filtered.length} treatment${filtered.length === 1 ? '' : 's'} matching "${query}"`}
            </h2>
            {filtered.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((t) => <TreatmentCardX key={t.slug} t={t} />)}
              </div>
            )}
            {filtered.length === 0 && (
              <a
                href="/#request-appointment"
                className="inline-flex items-center gap-2 text-[#1FA7A1] font-semibold hover:underline"
              >
                Request a consultation and we'll find the right treatment for you
                <ArrowRight className="h-4 w-4" />
              </a>
            )}
          </div>
        </section>
      )}

      {/* ── CATEGORY-GROUPED CATALOG ────────────────────────────────────── */}
      {!filtered && CATEGORIES.map((category, idx) => {
        const treatments = TREATMENTS.filter((t) => t.category === category.slug);
        if (treatments.length === 0) return null;
        return (
          <section
            key={category.slug}
            id={`cat-${category.slug}`}
            className={`py-16 ${idx % 2 === 0 ? 'bg-white' : ''}`}
            style={idx % 2 === 1 ? { background: '#F8FAFC' } : {}}
            data-testid={`category-section-${category.slug}`}
          >
            <div className="container mx-auto px-4 max-w-6xl">
              <CategoryHeader category={category} count={treatments.length} resolveImage={getCategoryImage} />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {treatments.map((t) => <TreatmentCardX key={t.slug} t={t} />)}
              </div>
            </div>
          </section>
        );
      })}

      {/* ── BOTTOM CTA ──────────────────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <div
            className="rounded-[28px] px-8 py-12 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0f2b55 0%, #1E5BA8 55%, #1FA7A1 100%)' }}
          >
            <p className="text-[11px] font-bold text-white/60 uppercase tracking-widest mb-3">Talk to VietClinics</p>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 tracking-tight">
              Not sure which treatment fits?
            </h2>
            <p className="text-white/80 text-sm mb-8 max-w-xl mx-auto leading-relaxed">
              Our concierge team matches you with the right treatment and verified specialists — for free.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a
                href="/#request-appointment"
                className="bg-white text-[#1FA7A1] font-bold rounded-full px-7 py-3.5 inline-flex items-center gap-2 hover:-translate-y-0.5 transition-all"
                data-testid="treatments-bottom-request"
              >
                Get Personalized Quote <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/find-best-treatment"
                className="text-white font-semibold rounded-full px-7 py-3.5 border border-white/30 hover:bg-white/15 backdrop-blur-sm inline-flex items-center gap-2 transition-all"
                data-testid="treatments-bottom-ai"
              >
                <MessageCircle className="h-4 w-4" />
                Talk to our AI Concierge
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const CategoryHeader = ({ category, count, resolveImage }) => {
  const landing = getCategoryContent(category.slug);
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
      <div className="flex items-center gap-5">
        <div
          className="hidden sm:block w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${category.accent.from}, ${category.accent.to})` }}
        >
          <img
            src={resolveImage ? resolveImage(category.slug, category.image) : category.image}
            alt={category.name}
            className="w-full h-full object-cover opacity-70"
            onError={(e) => { e.target.src = category.image; }}
          />
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: category.accent.from }}>
            {count} Treatment{count === 1 ? '' : 's'}
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
            {category.name}
          </h2>
          <p className="text-slate-500 mt-1 text-sm sm:text-base max-w-xl leading-relaxed">
            {category.description}
          </p>
        </div>
      </div>
      {landing && (
        <Link
          to={landing.urlSlug}
          className="self-start sm:self-end inline-flex items-center gap-1.5 text-sm font-bold text-[#1FA7A1] hover:gap-2 transition-all"
          data-testid={`category-landing-link-${category.slug}`}
        >
          Read the full {category.name.toLowerCase()} guide
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
};

const TreatmentCardX = ({ t }) => (
  <Link
    to={`/treatments/${t.slug}`}
    className="group bg-white rounded-[20px] overflow-hidden border border-slate-100 hover:-translate-y-1 transition-all duration-300 flex flex-col"
    style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
    data-testid={`treatment-card-${t.slug}`}
  >
    <div className="relative h-44 overflow-hidden">
      <img
        src={t.image}
        alt={t.name}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800'; }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: `linear-gradient(180deg, transparent 50%, ${t.accent.from}AA 100%)` }}
      />
      {t.flagship && (
        <span className="absolute top-3 left-3 inline-flex items-center bg-white/95 backdrop-blur text-[10px] font-bold text-slate-700 px-2.5 py-1 rounded-full uppercase tracking-wider">
          <BookOpen className="h-3 w-3 mr-1 text-[#1FA7A1]" />
          Full Guide
        </span>
      )}
      <div className="absolute bottom-3 left-3 right-3 text-white">
        <div className="text-lg font-extrabold leading-tight">{t.name}</div>
        <div className="text-xs font-semibold text-white/90 mt-0.5">{t.startingPrice}</div>
      </div>
    </div>
    <div className="p-5 flex-1 flex flex-col">
      <p className="text-slate-600 text-sm leading-relaxed line-clamp-2 flex-1">{t.shortDesc}</p>
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
        <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
          <Clock className="h-3.5 w-3.5 text-slate-400" />
          {t.recoveryTime}
        </div>
        <span className="inline-flex items-center gap-1 text-xs font-bold text-[#1FA7A1] group-hover:gap-2 transition-all">
          {t.flagship ? 'Treatment Guide' : 'Explore Treatment'}
          <ChevronRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </div>
  </Link>
);

export default TreatmentsPage;
